import { OUTBOX_RETRY_MIN_MS, outboxExpired, outboxMaySend, outboxNextDelay, outboxRetries, outboxVerdict, outboxVisibleTo } from '@helpdesk/shared';
import { ApiRequestError, getJson, postJson, uploadFile } from '../api/client.ts';
import type { Me } from '../api/types.ts';
import { logoutPending } from './logout.ts';

/**
 * Offline-Ausgang für Meldungen (F21). Ohne Netz landet die Meldung samt Fotos
 * in IndexedDB. Gesendet wird aus jeder offenen Seite (`startOutboxSync`), auch ohne
 * Anmeldung. Background Sync bleibt draußen: iOS kennt es nicht, und den Besitzer
 * einer Meldung kann nur die offene Seite prüfen. Was der Server nicht annimmt,
 * bleibt liegen und ist sichtbar, bis der Melder entscheidet: Gelöscht wird eine
 * Meldung nur nach dem Senden, auf ausdrücklichen Klick oder nach Ablauf der Frist,
 * und dann bleibt ein sichtbarer Grabstein.
 */
export type OutboxState = 'waiting' | 'held' | 'rejected' | 'expired';

type ReportRef = {
  id: string;
  createdAt: string;
  /** `quick` = ohne Login (Upload-Token), `web` = angemeldet, `kiosk` = Hallen-Tablet. */
  kind: 'web' | 'quick' | 'kiosk';
  machineLabel?: string | null;
  /** Ticketnummer, sobald das Ticket besteht und nur noch Fotos ausstehen. */
  number?: string;
};

export type QueuedReport = ReportRef & {
  /** Nur `web`: Benutzer-ID der Person, die gespeichert hat. Einträge älterer Versionen haben das Feld nicht. */
  ownerUserId?: string;
  endpoint: string;
  body: Record<string, unknown>;
  photos: Blob[];
  /** Nach erfolgreicher Meldung, aber vor allen Fotos. */
  ticketId?: string;
  uploadToken?: string;
  uploaded?: number;
  /** `held` wartet auf die Anmeldung, `rejected` auf den Melder. Einträge älterer Versionen haben das Feld nicht. */
  state?: Exclude<OutboxState, 'expired'>;
  /** Servermeldung zum letzten Versuch. */
  reason?: string;
  /** Endgültig abgelehnte Fotos, damit „Erneut senden“ sie noch hat. */
  failedPhotos?: { photo: Blob; reason: string }[];
};

/** Grabstein: ohne Text, Fotos und Besitzer, damit nichts Persönliches auf dem Gerät bleibt. Wer ihn sieht, darf ihn bestätigen. */
export type ExpiredReport = ReportRef & { state: 'expired' };

export type PendingReport = QueuedReport | ExpiredReport;

/** Die Nummer gibt es nur in diesem Moment, der Eintrag ist danach weg. `seen`: Sie stand schon vor der Person. */
export type SentReport = { id: string; number: string; seen?: boolean };

export type OutboxSnapshot = {
  reports: PendingReport[];
  sent: SentReport[];
  /** `AppShell` ist offen und zeigt Zähler und Hinweis selbst. */
  hosted: boolean;
};

const DB = 'helpdesk-outbox';
const STORE = 'reports';
const SENT_HINT_MS = 10 * 60_000;

let snapshot: OutboxSnapshot = { reports: [], sent: [], hosted: false };
const listeners = new Set<() => void>();

function publish(next: Partial<OutboxSnapshot>): void {
  snapshot = { ...snapshot, ...next };
  listeners.forEach((listener) => listener());
}

export const outboxSnapshot = (): OutboxSnapshot => snapshot;

export function subscribeOutbox(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Andere Fenster erfahren so, was hier gesendet wurde: Ihre Bestätigung wartete sonst ewig auf die Nummer. */
const peers = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(DB);

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: 'id' });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  // Eine Verbindung je Zugriff: Eine gehaltene überlebt den Ruhezustand auf iOS nicht
  // verlässlich und würde ein künftiges Versions-Upgrade blockieren.
  const db = await open();
  try {
    return await new Promise<T>((resolve, reject) => {
      const t = db.transaction(STORE, mode);
      const req = fn(t.objectStore(STORE));
      req.onerror = () => reject(req.error);
      // Erst der Commit zählt: Speichermangel bricht die Transaktion oft noch nach dem success-Ereignis ab.
      t.oncomplete = () => resolve(req.result);
      t.onabort = () => reject(t.error);
    });
  } finally {
    db.close();
  }
}

/** `id` nur, wenn der Aufrufer schon einen Schlüssel für diese Meldung führt. */
export async function queueReport(report: Omit<QueuedReport, 'id' | 'createdAt'> & { id?: string }): Promise<string> {
  const item: QueuedReport = { ...report, id: report.id ?? crypto.randomUUID(), createdAt: new Date().toISOString() };
  await tx('readwrite', (s) => s.put(item));
  // Sofort im Stand: Die Bestätigung erscheint gleich danach und fragt nach genau diesem Eintrag.
  publish({ reports: [...snapshot.reports, item] });
  changed();
  flushNow();
  return item.id;
}

/** Älteste Meldung zuerst: Die Schlüssel sind zufällig und sagen nichts über die Reihenfolge. */
async function readReports(): Promise<PendingReport[]> {
  const reports = await tx('readonly', (s) => s.getAll() as IDBRequest<PendingReport[]>);
  return reports.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/** Für einen Durchgang: Ist der Speicher nicht lesbar, gibt es diesmal nichts zu tun. */
export async function pendingReports(): Promise<PendingReport[]> {
  try {
    return await readReports();
  } catch {
    return [];
  }
}

let latestRead = 0;

/**
 * Überholt eine spätere Lesung eine frühere, zählt die spätere. `null`: Die Lesung ist gescheitert, und das
 * sagt nichts über den Ausgang. Der Stand bleibt, sonst hieße es, eine liegende Meldung sei nicht mehr da.
 */
async function refreshReports(): Promise<PendingReport[] | null> {
  const read = ++latestRead;
  try {
    const reports = await readReports();
    if (read === latestRead) publish({ reports });
    return reports;
  } catch {
    return null;
  }
}

function changed(): void {
  void refreshReports();
  peers?.postMessage(null);
}

function noteSent(report: SentReport): void {
  if (snapshot.sent.some((s) => s.id === report.id)) return;
  publish({ sent: [...snapshot.sent, report] });
  setTimeout(() => acknowledgeSent(report.id), SENT_HINT_MS);
}

export function acknowledgeSent(id: string): void {
  if (!snapshot.sent.some((s) => s.id === id && !s.seen)) return;
  publish({ sent: snapshot.sent.map((s) => (s.id === id ? { ...s, seen: true } : s)) });
}

peers?.addEventListener('message', (event: MessageEvent<SentReport | null>) => {
  if (event.data) noteSent(event.data);
  void refreshReports();
});

/** Was noch auf dem Gerät bleibt, wenn diese Person sich abmeldet. */
export async function ownPendingCount(userId: string): Promise<number> {
  const reports = await pendingReports();
  return reports.filter((r) => r.kind === 'web' && r.state !== 'expired' && outboxVisibleTo(r, userId)).length;
}

/** Hält den Stand eines Sendeversuchs fest. */
async function saveProgress(item: PendingReport): Promise<void> {
  try {
    await tx('readwrite', (s) => s.put(item));
    changed();
  } catch {
    // Speicher voll: Der Durchgang läuft mit dem Eintrag im Speicher weiter. Ein Abbruch ließe ihn
    // ohne Ticketnummer liegen, und jeder weitere Durchgang legte dieselbe Meldung neu an.
  }
}

async function remove(id: string): Promise<void> {
  await tx('readwrite', (s) => s.delete(id));
  changed();
}

let lastTurn: Promise<unknown> = Promise.resolve();

/**
 * Nur eine Stelle zur Zeit fasst den Ausgang an, auch über Fenster hinweg: Sonst legen zwei Fenster
 * dieselbe Meldung an, oder ein laufender Durchgang schreibt einen gelöschten Eintrag zurück.
 * Gewartet wird bewusst: Ein Klick auf „Erneut senden“ darf nicht verfallen, nur weil ein anderes
 * Fenster gerade sendet. Ohne Web Locks bleibt die Reihenfolge immerhin im eigenen Fenster.
 */
function exclusive<T>(work: () => Promise<T>): Promise<T> {
  const turn = lastTurn.then(() => (navigator.locks ? navigator.locks.request(DB, work) : work()));
  lastTurn = turn.catch(() => undefined);
  return turn;
}

/** Macht aus jedem Eintrag jenseits der Frist einen Grabstein, auch aus abgelehnten und fremden. */
async function expireReports(): Promise<void> {
  for (const report of await pendingReports()) {
    if (report.state === 'expired' || !outboxExpired(report.createdAt)) continue;
    const { id, createdAt, kind, machineLabel, number } = report;
    await saveProgress({ id, createdAt, kind, machineLabel, number, state: 'expired' });
  }
}

/** Legt das Ticket an und sendet die Fotos. `true`, wenn der Eintrag damit erledigt ist. */
async function deliver(item: QueuedReport): Promise<boolean> {
  item.state = 'waiting';
  item.reason = undefined;
  if (!item.ticketId) {
    const created = await postJson<{ id?: string; ticketId?: string; uploadToken?: string; display?: string }>(item.endpoint, item.body);
    item.ticketId = created.ticketId ?? created.id;
    item.uploadToken = created.uploadToken;
    item.number = created.display;
    item.uploaded = 0;
    await saveProgress(item);
  }
  const url = item.kind === 'web' ? `/api/tickets/${item.ticketId}/attachments` : `/api/quick/tickets/${item.ticketId}/attachments`;
  const fields: Record<string, string> = typeof item.body.name === 'string' ? { name: item.body.name } : {};
  const headers: Record<string, string> = item.uploadToken ? { 'x-upload-token': item.uploadToken } : {};
  for (let i = item.uploaded ?? 0; i < item.photos.length; i++) {
    const photo = item.photos[i]!;
    try {
      await uploadFile(url, photo, `foto-${i + 1}.jpg`, fields, headers);
    } catch (err) {
      if (!(err instanceof ApiRequestError) || outboxVerdict(err.status, err.code) !== 'reject') throw err;
      item.failedPhotos = [...(item.failedPhotos ?? []), { photo, reason: err.message }];
    }
    item.uploaded = i + 1;
    await saveProgress(item);
  }
  if (item.failedPhotos?.length) {
    item.state = 'rejected';
    await saveProgress(item);
    return false;
  }
  if (item.number) {
    const sent = { id: item.id, number: item.number };
    noteSent(sent);
    peers?.postMessage(sent);
  }
  await remove(item.id);
  return true;
}

async function currentAccount(): Promise<Me['user'] | null> {
  // Das Cookie gilt noch, aber die Person hat sich abgemeldet: Ihre Meldung wartet auf die nächste Anmeldung.
  if (logoutPending()) return null;
  try {
    return (await getJson<Me>('/api/auth/me')).user;
  } catch (err) {
    if (err instanceof ApiRequestError && err.status === 401) return null;
    throw err;
  }
}

/** Schreibt nur bei einem Wechsel: Der Eintrag bleibt liegen, und jeder weitere Durchgang kommt wieder hier vorbei. */
async function hold(item: QueuedReport): Promise<void> {
  if (item.state === 'held' && !item.reason) return;
  item.state = 'held';
  item.reason = undefined;
  await saveProgress(item);
}

async function sendAll(): Promise<number> {
  await expireReports();
  if (!navigator.onLine) return 0;
  let sent = 0;
  let account: Promise<Me['user'] | null> | undefined;
  for (const item of await pendingReports()) {
    if (item.state === 'rejected' || item.state === 'expired') continue;
    try {
      if (item.kind === 'web' && !outboxMaySend(item, await (account ??= currentAccount()))) {
        await hold(item);
        continue;
      }
      if (await deliver(item)) sent++;
    } catch (err) {
      // Keine Antwort unseres Servers, auch eine WLAN-Anmeldeseite statt JSON: beim nächsten Versuch weiter.
      if (!(err instanceof ApiRequestError) || err.code === 'unreadable_response') break;
      const verdict = outboxVerdict(err.status, err.code);
      item.state = verdict === 'hold' ? 'held' : verdict === 'reject' ? 'rejected' : 'waiting';
      item.reason = err.message;
      await saveProgress(item);
    }
  }
  return sent;
}

let flushRun: Promise<number> | null = null;
let flushAgain = false;
let retryDelay = OUTBOX_RETRY_MIN_MS;
let retryTimer: ReturnType<typeof setTimeout> | undefined;

async function drain(): Promise<number> {
  let sent = 0;
  do {
    flushAgain = false;
    sent += await exclusive(sendAll);
  } while (flushAgain);
  return sent;
}

/**
 * Plant den nächsten Versuch, solange etwas rausgehen kann. Im WLAN ohne Uplink bleibt
 * `navigator.onLine` wahr und `online` feuert nie, ohne Timer käme kein zweiter Versuch.
 * Darum endet die Kette nur, wenn der Ausgang nachweislich nichts mehr hergibt, nie an einer gescheiterten Lesung.
 */
async function planRetry(sent: number): Promise<void> {
  if (sent) retryDelay = OUTBOX_RETRY_MIN_MS;
  const reports = await refreshReports();
  clearTimeout(retryTimer);
  if (reports && !reports.some(outboxRetries)) {
    retryDelay = OUTBOX_RETRY_MIN_MS;
    return;
  }
  retryTimer = setTimeout(() => void flushOutbox(), retryDelay);
  retryDelay = outboxNextDelay(retryDelay);
}

/** Sendet alle wartenden Meldungen; bleibt bei fehlendem Netz still. Abgelehnte und fremde lässt er liegen. */
export function flushOutbox(): Promise<number> {
  if (flushRun) {
    // Der laufende Durchgang kennt nur, was bei seinem Start im Ausgang lag.
    flushAgain = true;
    return flushRun;
  }
  flushRun = drain()
    .catch(() => 0)
    .then(async (sent) => {
      flushRun = null;
      await planRetry(sent);
      return sent;
    });
  return flushRun;
}

/** Ein äußerer Anlass: gleich versuchen, und der Abstand beginnt von vorn. */
function flushNow(): void {
  retryDelay = OUTBOX_RETRY_MIN_MS;
  void flushOutbox();
}

/** Alle Auslöser des Nachsendens. Läuft an der Wurzelroute, also auch auf Kiosk, Schnellmeldung und Anmeldeseite. */
export function startOutboxSync(): () => void {
  const onVisible = () => {
    if (document.visibilityState === 'visible') flushNow();
  };
  const onShow = (event: PageTransitionEvent) => {
    if (event.persisted) flushNow();
  };
  void refreshReports();
  flushNow();
  window.addEventListener('online', flushNow);
  window.addEventListener('pageshow', onShow);
  document.addEventListener('visibilitychange', onVisible);
  return () => {
    clearTimeout(retryTimer);
    window.removeEventListener('online', flushNow);
    window.removeEventListener('pageshow', onShow);
    document.removeEventListener('visibilitychange', onVisible);
  };
}

/** Der Start von `AppShell` heißt auch: Es gibt eine Sitzung, gehaltene Meldungen dieser Person dürfen raus. */
export function hostOutbox(): () => void {
  publish({ hosted: true });
  flushNow();
  return () => publish({ hosted: false });
}

const EXPIRY_CHECK_MS = 60_000;

/**
 * Hält die Frist auf jeder Seite ein, auch wenn nichts gesendet wird: Ein Kiosk bleibt tagelang offen und lädt nie neu.
 * Ein laufender Durchgang geht vor, er schriebe den Eintrag sonst mit seinem Stand zurück.
 */
export function watchOutboxExpiry(): void {
  const check = () => void exclusive(expireReports);
  check();
  setInterval(check, EXPIRY_CHECK_MS);
}

/**
 * Holt einen Eintrag zurück und sendet gleich. Abgelehnte Fotos kommen dabei noch einmal an die Reihe.
 * Altbestand ohne Besitzer übernimmt hier die Person, die geklickt hat.
 */
export async function retryReport(id: string, userId: string): Promise<void> {
  await exclusive(async () => {
    const item = await tx('readonly', (s) => s.get(id) as IDBRequest<PendingReport | undefined>);
    if (!item || item.state === 'expired') return;
    if (item.kind === 'web') item.ownerUserId ??= userId;
    if (item.state === 'rejected' && item.failedPhotos?.length) {
      item.photos = item.failedPhotos.map((f) => f.photo);
      item.uploaded = 0;
      item.failedPhotos = undefined;
    }
    item.state = 'waiting';
    item.reason = undefined;
    await tx('readwrite', (s) => s.put(item));
    changed();
  });
  await flushOutbox();
}

/** Wartet einen laufenden Durchgang ab: Er schriebe den Eintrag sonst mit seinem Stand zurück. */
export function discardReport(id: string): Promise<void> {
  return exclusive(() => remove(id));
}
